import * as React from 'react';
import {useCallback, useState} from 'react';
import {Alert} from '@mui/material';
import {ConfirmDialog} from 'components/ConfirmDialog';
import {useAppTranslation} from 'services/i18n';
import {useAppDispatch} from 'store';
import {approveApprovalRequest, bulkApproveApprovalRequests} from 'store/approvals';
import {addMessage} from 'store/localApp';
import {isApiResultError} from '../../helpers/api';
import {approvalDecisionError, type ApprovalRequestInfo} from 'model/approval';

/** Error codes bulk_approve reports per item; each has its own message. */
const BULK_ERRORS = ['not_found', 'invalid', 'not_authorized', 'incomplete'];

export const ApprovalApproveDialog = ({items, isBulk, onSuccess, onClose}: {
    items: ApprovalRequestInfo[],
    /**
     * Set when the selection bar started it, not the tick on a row -- a selection
     * of one is still a bulk confirmation and still goes through bulk_approve,
     * whose partial result is what the bar reports.
     */
    isBulk?: boolean,
    onSuccess: () => void,
    onClose: () => void
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);
    /**
     * A refused decision is reported here rather than as a toast: the dialog
     * stays open with the reason on it until the manager dismisses it, and the
     * queue behind it is left alone.
     */
    const [error, setError] = useState<string>();

    const reportBulkResult = useCallback((failed: { id: number, error: string }[], approvedCount: number) => {
        if (!failed.length) {
            dispatch(addMessage({
                severity: 'success',
                title: t('approvals.approve.bulkDone', {count: approvedCount})
            }));
            return;
        }
        // each item is re-verified server side, so a stale or incomplete one can
        // drop out of an otherwise valid batch -- name the reasons, not just the count
        const reasons = BULK_ERRORS
            .filter(code => failed.some(f => f.error === code))
            .map(code => t('approvals.approve.error.' + code, {n: failed.filter(f => f.error === code).length}))
            .join(' ');
        dispatch(addMessage({
            severity: approvedCount ? 'warning' : 'error',
            title: t('approvals.approve.bulkPartial', {approved: approvedCount, failed: failed.length}) + ' ' + reasons
        }));
    }, [dispatch, t]);

    const handleConfirm = useCallback(async () => {
        // bulk_approve reads an empty id list as "approve everything approvable in
        // the whole space", so an empty selection must never reach it
        if (!items.length) {
            onClose();
            return;
        }
        setIsSaving(true);
        setError(undefined);
        const result = isBulk
            ? await dispatch(bulkApproveApprovalRequests({
                body: {approval_request_ids: items.map(item => item.id)}
            }))
            : await dispatch(approveApprovalRequest({id: String(items[0].id)}));
        setIsSaving(false);

        if (isApiResultError(result)) {
            setError(approvalDecisionError(result.payload as any, t, isBulk));
            return;
        }
        if (isBulk) {
            const payload = result.payload as { approved?: unknown[], failed: { id: number, error: string }[] };
            reportBulkResult(payload.failed || [], payload.approved?.length || 0);
        } else {
            dispatch(addMessage({severity: 'success', title: t('approvals.approve.done')}));
        }
        onSuccess();
        onClose();
    }, [isBulk, items, dispatch, reportBulkResult, onSuccess, onClose, t]);

    return (
        <ConfirmDialog
            title={isBulk ? 'approvals.approve.bulkTitle' : 'approvals.approve.title'}
            action={t('approvals.approve.confirm')}
            onConfirm={handleConfirm}
            onCancel={onClose}
            isSaving={isSaving}
        >
            <>
                <p>{isBulk
                    ? t('approvals.approve.bulkText', {count: items.length})
                    : t('approvals.approve.text')}</p>
                {!!error && <Alert severity={'error'} onClose={() => setError(undefined)}>{error}</Alert>}
            </>
        </ConfirmDialog>
    );
};
